import {
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from "effector";
import { status } from "patronum";
import { AxiosError } from "axios";

import * as api from "src/shared/api";

import { createAsyncQueue } from "src/lib/create-async-queue";
import { createGate } from "effector-react";
import { removeItemOnce } from "src/lib";
import { $user } from "src/entities/user/model";
import { DEFAULT_MAP_CENTER } from "src/shared/constants";
import { fetchReportsFx } from "../history-screen/model";

export const ReportGate = createGate("");
export const saveAddress = createEvent<string>("save address");
export const saveDescription = createEvent<string>("save description");
export const addMedia = createEvent<File[]>("add file");
export const sendReport = createEvent("send media");
export const createNew = createEvent("create new");
export const savePhone = createEvent<string>("save phone");
export const deleteMedia = createEvent<File>("remove file");
export const resetError = createEvent("reset error");
export const coordinatesSettled = createEvent<{
  accessor: "latitude" | "longitude";
  value: number;
}>();

export const $coordinates = createStore({
  latitude: DEFAULT_MAP_CENTER.lat,
  longitude: DEFAULT_MAP_CENTER.lng,
}).on(coordinatesSettled, (currentCenter, { accessor, value }) => ({
  ...currentCenter,
  [accessor]: value,
}));

export const $isSended = createStore(false).reset(createNew);
export const $error = createStore<string | null>(null).reset(resetError);
export const $privateMapSettings = createStore<Record<string, any>>({});

// TODO: add types
export const $lastAddress = createStore<string>("")
  .on(saveAddress, (_, lastAddress) => lastAddress)
  .reset(createNew);

export const $description = createStore<string>("")
  .on(saveDescription, (_, description) => description)
  .reset(createNew);

export const $media = createStore<File[]>([])
  .on(addMedia, (prevFiles, file) => [...prevFiles, ...file])
  .on(deleteMedia, (medias, deleted) => {
    return [...removeItemOnce(medias, deleted)];
  })
  .reset(createNew);

export const $phone = createStore<string>("").on(
  savePhone,
  (_, phone) => phone
);

const loadPrivateMapSettingsFx = createEffect(async function () {
  const response = await api.get("react_arcgis_settings_3/");

  return await response.data[0];
});

export const $isPrivateMapSettingsLoading = loadPrivateMapSettingsFx.pending;

sample({
  clock: [ReportGate.open, $user],
  source: $user,
  fn: ({ phone }) => phone,
  target: $phone,
});

sample({
  clock: ReportGate.open,
  target: loadPrivateMapSettingsFx,
});

sample({
  clock: loadPrivateMapSettingsFx.doneData,
  target: $privateMapSettings,
});

const postReportFx = createEffect<
  {
    description: string;
    media: File[];
    address: string;
    phone: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  },
  Promise<any>,
  AxiosError
>(async ({ description, media, address, phone, coordinates }) => {
  const createReport = await api.post("reports/", {
    address,
    description,
    phone,

    ...coordinates,
    ...(media?.length === 0 ? { last: 1 } : {}),
    source: "web",
  });

  const reportId = await createReport.data.id;

  const queue = createAsyncQueue();

  media.forEach((file, idx) => {
    const data = new FormData();

    const isLast = idx === media.length - 1;
    data.append("file", file);

    if (isLast) data.append("last", "1");

    const putImage = async () => {
      await api.put(`reports/${reportId}/media/`, data);
    };

    queue.push(putImage);
  });

  return await queue.flush();
});

const $formData = combine(
  $lastAddress,
  $description,
  $media,
  $phone,
  $coordinates
);

sample({
  clock: sendReport,
  source: $formData,
  fn: ([address, description, media, phone, coordinates]) => ({
    address,
    description,
    media,
    phone,
    coordinates: {
      lat: coordinates.latitude,
      lng: coordinates.longitude,
    },
  }),
  target: postReportFx,
});

sample({
  clock: [postReportFx.done],
  fn: () => true,
  target: $isSended,
});

sample({
  clock: postReportFx.failData,
  fn: (error: AxiosError) => {
    return error.response?.data.error as string;
  },
  target: $error,
});

sample({
  clock: [ReportGate.close],
  target: createNew,
});

sample({
  clock: ReportGate.open,
  source: $user,
  filter: (user) => {
    return !!user?.id;
  },
  target: [fetchReportsFx],
});

const $status = status({ effect: postReportFx });

export const $isLoading = $status.map((status) => status === "pending");
